import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArenaLogo } from '@/components/common/ArenaLogo';

export function AdminInvite() {
  const { token } = useParams<{ token: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trialDays, setTrialDays] = useState<number | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!token) return;
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/brand-admin-invitation/details/${token}`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (typeof data?.trialDays === "number") {
          setTrialDays(data.trialDays);
        }
      } catch {
        // swallow – fallback copy will be used
      }
    };
    fetchDetails();
  }, [token]);

  const handleContinue = async () => {
    if (!token) {
      setError('Invalid invite link');
      return;
    }
    try {
      setIsLoading(true);
      setError(null);

      // Persist the admin invite token locally so we can detect it during onboarding
      try {
        if (token) {
          localStorage.setItem('rs_admin_invite_token', token);
        }
      } catch {
        // non-fatal if localStorage isn't available
      }

      // Small UX hint before redirecting
      toast.success(
        "We'll send you to the Shopify App Store to install Reveshare. After installing, you'll be guided through a short onboarding."
      );

      const appStoreUrl =
        import.meta.env.VITE_SHOPIFY_APP_STORE_URL ||
        "https://apps.shopify.com/reveshare";
      window.location.href = appStoreUrl;
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Failed to continue');
      setError(err?.message || 'Failed to continue');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto flex items-center justify-center min-h-screen py-8 px-4">
      <div className="w-full max-w-[520px] flex flex-col items-center justify-center gap-8 p-6 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100]">
        <div className="w-full max-w-[420px]">
          <div className="flex flex-col items-center justify-center gap-6 mb-4">
            <ArenaLogo className="w-12 border border-black mx-auto mb-4 rounded-[12px] bg-[#f6f6f6]" />
            <h1 className="font-bold text-[24px] text-foreground/60 leading-[24px] tracking-[0.16px] text-center">
              Install Reveshare
            </h1>
            <p className="font-normal text-[14px] w-[330px] text-center text-muted-foreground">
              You've received an admin invite. Click below to continue and install Reveshare from the Shopify App Store.
            </p>
            <p className="font-normal text-[13px] w-[330px] text-center text-[#6B7280]">
              {trialDays
                ? `Enjoy a ${trialDays}-day free trial when you install via this invite.`
                : "Enjoy a free trial when you install via this invite."}
            </p>

            {error && (
              <p className="text-red-600 text-sm">{error}</p>
            )}

            <button
              type="button"
              onClick={handleContinue}
              disabled={isLoading}
              className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Redirecting...
                </div>
              ) : (
                'Continue to installation'
              )}
            </button>
            <p className="text-xs text-muted-foreground text-center">
              This link is single-use and valid for one installation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


